import React, { useState, useMemo } from 'react';
import {
    Button,
    IconButton,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    InputAdornment,
    Collapse,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { useTranslation } from 'react-i18next';
import AssetService from '../../../Services/assets.service';
import DropzoneArea from '../../ClientDetails/SubComponents/DropzoneArea';

const AssetImportModal = ({ open, onClose, caseId, token, refreshAssets }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();

    const [file, setFile] = useState(null);
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [importResult, setImportResult] = useState(null);
    const [importYear, setImportYear] = useState('');

    const handleFileDrop = (files) => {
        if (files?.length) setFile(files[0]);
    };

    const handlePreview = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            setPreviewDone(false);
            setImportResult(null);
            const res = await AssetService.PreviewAssetImport(caseId, file, token);
            const data = res?.data || res;
            setPreviewData(data);
            if (data?.detectedYear) setImportYear(String(data.detectedYear));
            const expanded = {};
            (data?.groups || []).forEach((_, i) => { expanded[i] = true; });
            setExpandedGroups(expanded);
            setPreviewDone(true);
        } catch (err) {
            console.error('Asset import preview failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleImport = async () => {
        if (!file) return;
        try {
            setProcessing(true);
            const yearNum = importYear ? parseInt(importYear, 10) : null;
            const res = await AssetService.ImportAssets(caseId, file, token, yearNum);
            setImportResult(res);
            if (refreshAssets) await refreshAssets();
        } catch (err) {
            console.error('Asset import failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewDone(false);
        setPreviewData(null);
        setExpandedGroups({});
        setSearchTerm('');
        setImportResult(null);
        setImportYear('');
        onClose();
    };

    const toggleGroup = (idx) => {
        setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const filteredGroups = useMemo(() => {
        if (!previewData?.groups) return [];
        if (!searchTerm.trim()) return previewData.groups;
        const term = searchTerm.trim().toLowerCase();
        return previewData.groups.map(group => ({
            ...group,
            assets: group.assets.filter(a =>
                (a.name || '').toLowerCase().includes(term) ||
                (a.description || '').toLowerCase().includes(term) ||
                String(a.originalPrice ?? '').includes(term)
            )
        })).filter(g => g.assets.length > 0 || g.groupName.toLowerCase().includes(term));
    }, [previewData, searchTerm]);

    if (!open) return null;

    return (
        <div
            className="download-modal-overlay"
            onClick={handleClose}
            style={{ zIndex: 2000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '40px', overflow: 'auto' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    maxWidth: previewDone ? '1200px' : '500px',
                    width: '95%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    position: 'relative'
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                    <h4 style={{ margin: 0, color: '#304FFF', fontWeight: 600 }}>
                        {t('ImportAssets') || 'ייבוא נכסים מ-Excel'}
                    </h4>
                    <IconButton onClick={handleClose} sx={{ padding: '4px' }}>
                        <CloseIcon />
                    </IconButton>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                        {t('AssetsFile') || 'קובץ נכסים'} (.xlsx/.xls)
                    </Typography>
                    <DropzoneArea
                        key={file ? file.name : 'asset-import-empty'}
                        onDrop={handleFileDrop}
                        accept=".xls,.xlsx"
                        filesLimit={1}
                    />
                    {file && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            {t('FileSelected')}: {file.name}
                        </Typography>
                    )}
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                    <Button
                        variant="contained"
                        onClick={handlePreview}
                        disabled={!file || processing}
                        sx={{ backgroundColor: '#304FFF', '&:hover': { backgroundColor: '#2040DD' } }}
                    >
                        {processing && !previewDone ? (t('Processing') || 'מעבד...') : (t('Preview') || 'תצוגה מקדימה')}
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        disabled={!previewDone || processing || !!importResult}
                        onClick={handleImport}
                    >
                        {processing ? (t('Processing') || 'מעבד...') : (t('Import') || 'ייבוא')}
                    </Button>
                </div>

                {importResult && (
                    <div style={{ padding: 12, borderRadius: 8, marginBottom: 16, background: importResult.success ? '#e8f5e9' : '#ffebee' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {importResult.success
                                ? `${t('ImportComplete') || 'הייבוא הושלם'}: ${importResult.groupsCreated || 0} ${t('Groups') || 'קבוצות'}, ${importResult.assetsCreated || 0} ${t('Assets') || 'נכסים'}, ${importResult.duplicates || 0} ${t('DuplicateRows') || 'כפילויות'}`
                                : `${t('ImportFailed') || 'הייבוא נכשל'}: ${importResult.error || ''}`
                            }
                        </Typography>
                        {importResult.errors?.length > 0 && (
                            <ul style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: '13px', color: '#c62828' }}>
                                {importResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                            </ul>
                        )}
                    </div>
                )}

                {previewDone && previewData && (
                    <>
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16,direction: direction }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                                {t('Groups') || 'קבוצות'}: <strong>{previewData.totalGroups}</strong>
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                                {t('TotalAssets') || 'סה"כ נכסים'}: <strong>{previewData.totalAssets}</strong>
                            </Typography>
                            {previewData.duplicateAssets > 0 && (
                                <Typography variant="body2" sx={{ color: '#f57c00' }}>
                                    {t('DuplicateRows') || 'כפילויות'}: <strong>{previewData.duplicateAssets}</strong>
                                </Typography>
                            )}
                            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                                {t('NewRowsToImport') || 'חדשים לייבוא'}: <strong>{previewData.totalAssets - previewData.duplicateAssets}</strong>
                            </Typography>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                                    {t('ReportYear') || 'שנת דוח'}:
                                </Typography>
                                <TextField
                                    size="small"
                                    type="number"
                                    value={importYear}
                                    onChange={(e) => setImportYear(e.target.value)}
                                    sx={{ width: 100, '& .MuiInputBase-input': { py: 0.5, fontSize: 13, textAlign: 'center' } }}
                                    placeholder="2024"
                                />
                            </div>
                        </div>

                        {/* New Asset Groups Summary */}
                        {previewData.groups?.length > 0 && (
                            <Paper
                                variant="outlined"
                                sx={{
                                    mb: 2, borderRadius: '8px', overflow: 'hidden',
                                    border: '1px solid #c5ceff', direction: direction,
                                }}
                            >
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '8px 16px', background: '#e8ecf8',
                                }}>
                                    <CreateNewFolderIcon sx={{ color: '#304FFF', fontSize: 18 }} />
                                    <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1a237e' }}>
                                        {t('AssetGroupsToCreate') || 'קבוצות נכסים שייווצרו'} ({previewData.groups.length})
                                    </Typography>
                                </div>
                                <Table size="small" dir={direction}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>#</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>{t('GroupName') || 'שם קבוצה'}</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>{t('DepreciationPercentage') || '% פחת'}</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>{t('AssetsCount') || 'מספר נכסים'}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.groups.map((group, idx) => (
                                            <TableRow key={idx} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                                <TableCell sx={{ fontSize: 12, py: 0.5 }}>{idx + 1}</TableCell>
                                                <TableCell sx={{ fontSize: 12, py: 0.5, fontWeight: 500 }}>{group.groupName}</TableCell>
                                                <TableCell sx={{ fontSize: 12, py: 0.5, color: '#6a1b9a' }}>{group.depreciationPercentage}%</TableCell>
                                                <TableCell sx={{ fontSize: 12, py: 0.5 }}>{group.assets?.length || 0}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        )}

                        <TextField
                            size="small"
                            placeholder={t('Search') || 'חיפוש'}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            sx={{ width: 250, mb: 2 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {filteredGroups.map((group, gIdx) => (
                            <Paper key={gIdx} variant="outlined" sx={{ mb: 2, borderRadius: '8px', overflow: 'hidden', direction: direction }} dir={direction}>
                                <div
                                    onClick={() => toggleGroup(gIdx)}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '10px 16px',
                                        background: '#BFC8FF',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {expandedGroups[gIdx] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    <Typography sx={{ fontWeight: 600, color: '#304FFF', fontSize: '14px' }}>
                                        {group.groupName} ({group.assets.length} {t('Assets') || 'נכסים'}) — {t('DepreciationPercentage') || '% פחת'}: {group.depreciationPercentage}%
                                    </Typography>
                                </div>
                                <Collapse in={expandedGroups[gIdx]}>
                                    <TableContainer sx={{ maxHeight: 400 }}>
                                        <Table size="small" stickyHeader dir={direction}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('AssetName') || 'שם'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('PurchaseDate') || 'תאריך רכישה'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('ActivationDate') || 'תאריך הפעלה'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('OriginalPrice') || 'מחיר מקורי'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('DepreciationPercentage') || '% פחת'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('AccumulatedDepreciation') || 'פחת שנצבר'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('DepreciationThisYear') || 'פחת השנה'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('Description') || 'הערות'}</TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{t('Status') || 'סטטוס'}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {group.assets.map((asset, aIdx) => (
                                                    <TableRow
                                                        key={aIdx}
                                                        hover
                                                        sx={asset.isDuplicate ? { backgroundColor: '#fff3e0' } : {}}
                                                    >
                                                        <TableCell>{aIdx + 1}</TableCell>
                                                        <TableCell>{asset.name || ''}</TableCell>
                                                        <TableCell>{asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : ''}</TableCell>
                                                        <TableCell>{asset.activationDate ? new Date(asset.activationDate).toLocaleDateString() : ''}</TableCell>
                                                        <TableCell>{asset.originalPrice?.toLocaleString() ?? ''}</TableCell>
                                                        <TableCell>{asset.depreciationPercentage ?? ''}</TableCell>
                                                        <TableCell>{asset.accumulatedDepreciation ? asset.accumulatedDepreciation.toLocaleString() : '—'}</TableCell>
                                                        <TableCell>{asset.depreciationThisYear ? asset.depreciationThisYear.toLocaleString() : '—'}</TableCell>
                                                        <TableCell>{asset.description || ''}</TableCell>
                                                        <TableCell>
                                                            {asset.isDuplicate
                                                                ? <span style={{ color: '#f57c00', fontWeight: 600 }}>{t('Duplicate') || 'כפילות'}</span>
                                                                : <span style={{ color: '#2e7d32' }}>{t('New') || 'חדש'}</span>
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Collapse>
                            </Paper>
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};

export default AssetImportModal;
